import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import {
  AgentDetails,
  SessionEntry,
  TeamDetails,
  type ChatMessage
} from '../../types/os';

export interface MentionMetadata {
  label: string
  external_id: string
  type: 'account' | 'contact'
  domain?: string
  avatar_url?: string | null
}

interface Store {
  hydrated: boolean
  setHydrated: () => void
  streamingErrorMessage: string
  setStreamingErrorMessage: (streamingErrorMessage: string) => void
  endpoints: {
    endpoint: string
    id__endpoint: string
  }[]
  setEndpoints: (
    endpoints: {
      endpoint: string
      id__endpoint: string
    }[]
  ) => void
  isStreaming: boolean
  setIsStreaming: (isStreaming: boolean) => void
  // Chat input state
  inputMessage: string
  setInputMessage: (text: string) => void
  mentions: MentionMetadata[]
  setMentions: (mentions: MentionMetadata[] | ((prev: MentionMetadata[]) => MentionMetadata[])) => void
  isEndpointActive: boolean
  setIsEndpointActive: (isActive: boolean) => void
  isEndpointLoading: boolean
  setIsEndpointLoading: (isLoading: boolean) => void
  messages: ChatMessage[]
  setMessages: (
    messages: ChatMessage[] | ((prevMessages: ChatMessage[]) => ChatMessage[])
  ) => void
  chatInputRef: React.RefObject<HTMLTextAreaElement | null>
  selectedEndpoint: string
  setSelectedEndpoint: (selectedEndpoint: string) => void
  agents: AgentDetails[]
  setAgents: (agents: AgentDetails[]) => void
  teams: TeamDetails[]
  setTeams: (teams: TeamDetails[]) => void
  selectedModel: string
  setSelectedModel: (model: string) => void
  mode: 'agent' | 'team'
  setMode: (mode: 'agent' | 'team') => void
  // Model configuration for AI requests. model_key is a registry key resolved
  // by the backend (routed via OpenRouter). provider/display_name are for UI.
  modelConfig: {
    model_key: string
    provider?: string
    display_name?: string
  }
  setModelConfig: (config: { model_key: string; provider?: string; display_name?: string }) => void,
  sessionId: string | null
  setSessionId: (id: string | null) => void
  brainAgentId: string | null
  setBrainAgentId: (id: string | null) => void
  sessionsData: SessionEntry[] | null
  setSessionsData: (
    sessionsData:
      | SessionEntry[]
      | ((prevSessions: SessionEntry[] | null) => SessionEntry[] | null)
  ) => void
  isSessionsLoading: boolean
  setIsSessionsLoading: (isSessionsLoading: boolean) => void
}

const useStore = create<Store>()(
  persist(
    (set) => ({
      hydrated: false,
      setHydrated: () => set({ hydrated: true }),
      streamingErrorMessage: '',
      setStreamingErrorMessage: (streamingErrorMessage) =>
        set(() => ({ streamingErrorMessage })),
      endpoints: [],
      setEndpoints: (endpoints) => set(() => ({ endpoints })),
      isStreaming: false,
      setIsStreaming: (isStreaming) => set(() => ({ isStreaming })),
      // Chat input state
      inputMessage: '',
      setInputMessage: (text: string) => set(() => ({ inputMessage: text })),
      mentions: [],
      setMentions: (mentions) =>
        set((state) => ({
          mentions: typeof mentions === 'function' ? mentions(state.mentions) : mentions
        })),
      isEndpointActive: false,
      setIsEndpointActive: (isActive) =>
        set(() => ({ isEndpointActive: isActive })),
      isEndpointLoading: true,
      setIsEndpointLoading: (isLoading) =>
        set(() => ({ isEndpointLoading: isLoading })),
      messages: [],
      setMessages: (messages) =>
        set((state) => ({
          messages:
            typeof messages === 'function' ? messages(state.messages) : messages
        })),
      chatInputRef: { current: null },
      selectedEndpoint: import.meta.env.VITE_AGENTOS_ENDPOINT || '',
      setSelectedEndpoint: (selectedEndpoint) =>
        set(() => ({ selectedEndpoint })),
      agents: [],
      setAgents: (agents) => set(() => {
        // Automatically set brain agent ID to 'cs_brain_skills'
        const brainAgent = agents.find((a) => a.id === 'cs_brain_skills') || agents[0];

        return {
          agents,
          brainAgentId: brainAgent?.id || null
        };
      }),
      teams: [],
      setTeams: (teams) => set({ teams }),
      selectedModel: '',
      setSelectedModel: (selectedModel) => set(() => ({ selectedModel })),
      mode: 'agent',
      setMode: (mode) => set(() => ({ mode })),
      // Default to Claude 4 Sonnet (registry key)
      modelConfig: {
        model_key: 'claude-sonnet-4-6',
        provider: 'anthropic',
        display_name: 'Claude 4 Sonnet'
      },
      setModelConfig: (modelConfig) => set(() => ({ modelConfig })),
      sessionId: null,
      setSessionId: (sessionId) => set(() => ({ sessionId })),
      brainAgentId: null,
      setBrainAgentId: (brainAgentId) => set(() => ({ brainAgentId })),
      sessionsData: null,
      setSessionsData: (sessionsData) =>
        set((state) => ({
          sessionsData:
            typeof sessionsData === 'function'
              ? sessionsData(state.sessionsData)
              : sessionsData
        })),
      isSessionsLoading: false,
      setIsSessionsLoading: (isSessionsLoading) =>
        set(() => ({ isSessionsLoading }))
    }),
    {
      name: 'endpoint-storage',
      version: 3,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        modelConfig: state.modelConfig
      }),
      migrate: (persistedState, version) => {
        let next = persistedState as Record<string, unknown> | undefined;
        if (version < 2 && next && typeof next === 'object') {
          next = { ...next };
          delete next.selectedEndpoint;
        }
        // v3: migrate legacy modelConfig {provider, model_id} -> {model_key}
        if (version < 3 && next && typeof next === 'object') {
          const mc = (next.modelConfig as Record<string, unknown>) || {};
          if (!mc.model_key) {
            next = {
              ...next,
              modelConfig: {
                model_key: 'claude-sonnet-4-6',
                provider: 'anthropic',
                display_name: 'Claude 4 Sonnet',
              },
            };
          }
        }
        return next;
      },
      onRehydrateStorage: () => (state) => {
        state?.setHydrated?.();
      }
    }
  )
);

export default useStore;
